import { useTranslation } from 'next-i18next'

import useLocale from '~/hooks/useLocale'
import { Appointment, SessionStatus } from '~/types/tutoring-api'
import { DATE_TIME_FORMAT } from '~/shared/constants/time.const'
import { useModifySessionMutation } from '~/redux/apis/session-api'

import AppointmentItem from './AppointmentItem'
import { Ordering } from './Appointments.types'
import { orderAppointments } from './Appointments.utils'

export default function Appointments({
  appointments = [],
  appointmentsOrdering = 'ascending',
  isTutor,
  onShowAllAppointmentsClick,
  selectedDate,
  showAllAppointments,
}: {
  appointments: Appointment[]
  appointmentsOrdering?: Ordering
  isTutor: boolean
  onShowAllAppointmentsClick: () => void
  selectedDate: Date
  showAllAppointments: boolean
}): React.ReactElement {
  const { t } = useTranslation('generics')
  const locale = useLocale()
  const readableDate = selectedDate.toLocaleDateString(locale, DATE_TIME_FORMAT.DAY_MONTH)
  const [modifySession] = useModifySessionMutation()

  const setSessionStatus = async (sessionId: number, status: SessionStatus): Promise<void> => {
    await modifySession({ id: sessionId, status })
  }

  return (
    <div className="flex flex-col">
      <div className="px-4">
        <div className="flex justify-between">
          <div className="text-lg font-bold text-hotpink">{t('appointments')}</div>
          <button
            className="font-medium focus:outline-none disabled:text-gray-light"
            disabled={showAllAppointments}
            onClick={onShowAllAppointmentsClick}
          >
            {t('showAll')}
          </button>
        </div>
        <span className="text-xs text-gray-light-fade">
          {showAllAppointments ? t('showingAllFromAndIncluding', { date: readableDate }) : readableDate}
        </span>
      </div>
      <div className="max-h-125 p-2 space-y-3 overflow-y-scroll">
        {orderAppointments(appointments, appointmentsOrdering).map((elem) => (
          <AppointmentItem
            appointment={elem}
            isTutor={isTutor}
            key={elem.startTime}
            setSessionStatus={setSessionStatus}
          />
        ))}
      </div>
    </div>
  )
}
